import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsIcon from '@mui/icons-material/Payments';
import HubIcon from '@mui/icons-material/Hub';
import SendIcon from '@mui/icons-material/Send';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import fpomIcon from './icons/fpom.svg';
import smIcon from './icons/sm.svg';
import fpgIcon from './icons/fpg.svg';
import feIcon from './icons/fe.svg';
import aaswIcon from './icons/aasw.svg';
import csfIcon from './icons/csf.svg';
import aoycIcon from './icons/aoyc.svg';
import csaIcon from './icons/csa.svg';
import srpIcon from './icons/srp.png';

import Container from '../../components/General/Container';
import { GeneralText } from '../../components/General/Text';
import {
  TitleWrapper,
  Wrapper,
  CareersText,
  ContentWrapper,
  PathCardsWrapper,
  PathCard,
  Pill,
  TagsWrapper,
  Tag,
  SectionsWrapper,
  DashedDivider,
  SectionTagline,
  SectionIntro,
  BenefitsGrid,
  BenefitCard,
  BenefitContent,
  BenefitIcon,
  BenefitIconImg,
  BenefitTitle,
  BenefitText,
  ApplyBox,
  ApplyTextGroup,
  ApplyTitle,
  ApplyText,
  ApplyButton,
  InfoCard,
  InfoHeading,
  InfoText,
  Banner,
  BannerTitle,
  BannerText,
} from './Careers.styled';

const APPLY_EMAIL = 'opportunity@study.tech';

const pathCards = [
  {
    label: 'Employed',
    title: 'Join the team',
    text: 'If you’re looking for a stable career with real purpose, a supportive team around you, and the chance to grow, you’ll feel right at home with us.',
    tags: ['Full-time', 'Part-time', 'Salary & Pension', 'CPD funded'],
  },
  {
    label: 'Freelance',
    title: 'Join our associate network',
    text: 'If you already work independently and want a reliable flow of students without the admin, our associate network is a natural fit.',
    tags: ['Flexible hours', 'Monthly payouts', 'No admin', 'Software included'],
  },
];

const trainerSections = [
  {
    id: 'employed',
    title: 'Employed AT trainers',
    tagline: 'A supportive, purposeful place to build your career.',
    intro:
      'As part of our in-house AT Training Team, you become a core part of how we support disabled students. We take care of the bookings and administration so you can focus on what you do best.',
    benefits: [
      {
        iconImg: fpomIcon,
        title: 'Financial peace of mind',
        text: 'Competitive fixed salary, bonus scheme, paid holiday allowance, workplace pension, and enhanced overtime rates.',
      },
      {
        iconImg: smIcon,
        title: 'Structured mentorship',
        text: 'A dedicated senior mentor for your first training sessions to guarantee a smooth, confident onboarding experience.',
      },
      {
        iconImg: fpgIcon,
        title: 'Funded professional growth',
        text: 'We design and fund your annual CPD schedule and pay for your specialist software accreditations.',
      },
      {
        iconImg: feIcon,
        title: 'Fully equipped',
        text: 'Everything you need is provided and looked after by us, including high-spec hardware and the assistive software you’ll use with students.',
      },
      {
        iconImg: aaswIcon,
        title: 'Accessible and supportive workplace',
        text: 'Comprehensive workplace adjustments, ergonomic hardware, and tailored software to ensure every team member has exactly what they need to thrive in their role.',
      },
    ],
    cta: {
      title: 'Ready to apply?',
      text: `Send your CV and brief expression of interest to ${APPLY_EMAIL} and a member of our team will be in touch.`,
      buttonLabel: 'Apply now',
    },
  },
  {
    id: 'freelance',
    title: 'Freelance AT trainers',
    tagline: 'Flexible, rewarding, and refreshingly straightforward.',
    intro:
      'If you already run your own training or coaching practice, becoming a Study Tech Associate gives you access to a steady pipeline of students across the UK - without the marketing or administration that comes with it.',
    benefits: [
      {
        iconImg: csfIcon,
        title: 'Consistent student flow',
        text: 'As an official SLC framework provider, we have a consistent flow of students who need AT training. We match you with students locally or remotely, so your diary stays active throughout the year.',
      },
      {
        iconImg: aoycIcon,
        title: 'Autonomy over your calendar',
        text: 'You choose when you work. Let us know your availability and accept or decline student referrals to suit your schedule - no pressure, no obligations.',
      },
      {
        iconImg: srpIcon,
        title: 'Simple, reliable payments',
        text: 'Manage your sessions through one dedicated online portal that takes the hassle out of the admin, so you can focus on the work you do best and get paid promptly for it.',
      },
      {
        iconImg: csaIcon,
        title: 'Complimentary software access',
        text: 'We make sure you have access to the assistive software you need to deliver great training to the students we refer to you.',
      },
      {
        iconImg: aaswIcon,
        title: 'An accessible associate network',
        text: 'Our trainer portals, invoicing systems, and student logging platforms comply with WCAG 2.1 accessibility standards. If you are a freelance trainer with a disability, we provide a friction-fee partnership where your insights are respected and leveraged to better support our students.',
      },
    ],
    cta: {
      title: 'Interested in partnering with us?',
      text: `Send your CV and brief expression of interest to ${APPLY_EMAIL} and a member of our team will be in touch.`,
      buttonLabel: 'Register interest',
    },
  },
];

const AccordionStyled = styled(Accordion)(() => ({
  background: 'transparent',
  boxShadow: 'none',
  color: '#FFFFFF',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
}));

const SummaryStyled = styled(AccordionSummary)(() => ({
  padding: 0,
  '& .MuiAccordionSummary-content': { margin: '8px 0' },
}));

const DetailsStyled = styled(AccordionDetails)(() => ({
  padding: 0,
}));

const ApplyCallToAction = ({ cta }) => (
  <ApplyBox>
    <ApplyTextGroup>
      <ApplyTitle>{cta.title}</ApplyTitle>
      <ApplyText>{cta.text}</ApplyText>
    </ApplyTextGroup>
    <ApplyButton href={`mailto:${APPLY_EMAIL}`} aria-label={cta.buttonLabel}>
      <SendIcon />
      {cta.buttonLabel}
    </ApplyButton>
  </ApplyBox>
);

const TrainerSection = ({ section, defaultExpanded }) => (
  <AccordionStyled disableGutters elevation={0} defaultExpanded={defaultExpanded}>
    <SummaryStyled expandIcon={<ExpandMoreIcon sx={{ color: '#FFFFFF', fontSize: 40 }} />}>
      <CareersText variant='jobTitle'>{section.title}</CareersText>
    </SummaryStyled>
    <DetailsStyled>
      <SectionTagline>{section.tagline}</SectionTagline>
      <SectionIntro>{section.intro}</SectionIntro>
      <BenefitsGrid>
        {section.benefits.map((benefit, index) => (
          <BenefitCard
            key={benefit.title}
            wide={section.benefits.length % 2 !== 0 && index === section.benefits.length - 1 ? 1 : undefined}
          >
            {benefit.iconImg ? (
              <BenefitIconImg src={benefit.iconImg} alt='' aria-hidden='true' />
            ) : (
              <BenefitIcon>{benefit.icon}</BenefitIcon>
            )}
            <BenefitContent>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitText>{benefit.text}</BenefitText>
            </BenefitContent>
          </BenefitCard>
        ))}
      </BenefitsGrid>
      <ApplyCallToAction cta={section.cta} />
      <DashedDivider />
    </DetailsStyled>
  </AccordionStyled>
);

const DsaSection = () => (
  <AccordionStyled disableGutters elevation={0}>
    <SummaryStyled expandIcon={<ExpandMoreIcon sx={{ color: '#FFFFFF', fontSize: 40 }} />}>
      <CareersText variant='jobTitle'>DSA Needs Assessors</CareersText>
    </SummaryStyled>
    <DetailsStyled>
      <InfoCard>
        <Pill>Expressions of interest welcome</Pill>
        <InfoHeading>We’d love to hear from you</InfoHeading>
        <InfoText>
          We’re not actively recruiting for DSA Needs Assessors right now, but we’re always glad to hear from experienced
          professionals. If you have a background in needs assessment and share our commitment to supporting disabled
          students, please do get in touch - we’ll keep your details on file and reach out when a suitable opportunity
          arises.
        </InfoText>
        <InfoText>
          We’re particularly interested in hearing from people with experience as a DSA Needs Assessor, who have a warm,
          approachable manner, and are confident working with students with a range of disabilities and needs.
        </InfoText>
      </InfoCard>
      <ApplyBox>
        <ApplyTextGroup>
          <ApplyTitle>Register your interest</ApplyTitle>
          <ApplyText>
            Send your CV along with a brief note about your background to {APPLY_EMAIL}
          </ApplyText>
        </ApplyTextGroup>
        <ApplyButton href={`mailto:${APPLY_EMAIL}`} aria-label='Get in touch'>
          <MailOutlineIcon />
          Get in touch
        </ApplyButton>
      </ApplyBox>
      <DashedDivider />
    </DetailsStyled>
  </AccordionStyled>
);

const Careers = () => {
  return (
    <Container>
      <Wrapper>
        <CareersText variant='h1'>CAREERS AT STUDY TECH</CareersText>
        <TitleWrapper>
          <CareersText variant='titleWhite'>Help us build a more </CareersText> <CareersText variant='titleYellow'>inclusive world</CareersText>
        </TitleWrapper>
        <CareersText variant='p' >We’re a friendly, growing team with shared commitment to making a real difference for disabled students. Whether you’re looking for a permanent role or a flexible partnership, we’d love to hear from you. </CareersText>

        <ContentWrapper>
          <CareersText variant='subTitleYellow'>HOW WOULD YOU LIKE TO WORK WITH US?</CareersText>
        </ContentWrapper>

        <ContentWrapper margin='8px 0 0 0'>
          <CareersText variant='jobTitle'>Choose your path</CareersText>
        </ContentWrapper>


        <ContentWrapper margin='8px 0 0 0'>
          <GeneralText variant='p'>We work with people on both an employed and freelance basis, and the experience is quite different depending on which suits you best. Have a read and see which feels like the right fit.</GeneralText>
        </ContentWrapper>

        <PathCardsWrapper>
          {pathCards.map(card => (
            <PathCard key={card.title}>
              <Pill>{card.label}</Pill>
              <BenefitTitle>{card.title}</BenefitTitle>
              <BenefitText>{card.text}</BenefitText>
              <TagsWrapper>
                {card.tags.map(tag => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </TagsWrapper>
            </PathCard>
          ))}
        </PathCardsWrapper>

        <SectionsWrapper>
          {trainerSections.map((section, index) => (
            <TrainerSection key={section.id} section={section} defaultExpanded={index === 0} />
          ))}
          <DsaSection />
        </SectionsWrapper>

        <Banner>
          <BannerTitle>We are a Disability Confident employer</BannerTitle>
          <BannerText>
            Inclusion isn’t just what we do for students. It’s how we operate as a team. We guarantee an interview to any
            disabled applicant who meets the minimum criteria, and we welcome conversations about adjustments at any stage.
          </BannerText>
        </Banner>

      </Wrapper>
    </Container>
  );
};

export default Careers;
